import React, { Fragment, useState, useEffect } from 'react';
import { Box, Button, CardMedia, Divider, Grid, Typography, CircularProgress, IconButton } from '@mui/material';
import Colors from '../../../Assets/Styles/Colors';
import BreadCrumbs from '../../../Components/BreadCrumbs/BreadCrumbs';
import AdBooksServices from '../../../Api/AdBooks/AdBooksServices';
import CategoriesServices from '../../../Api/AdBazar/Categories/CategoriesServices';
import SearchField from '../../../Components/Fields/SearchField';
import { CalendarMonth, Delete, KeyboardArrowLeft, WorkspacesOutlined } from '@mui/icons-material';
import { PrimaryButton, SecondaryButton } from '../../../Components/Buttons';
import { CircleLoading } from '../../../Components/Loaders/CircleLoading';
import { baseUrl } from '../../../../axios';
import Images from '../../../Assets/Images/Images';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../../../Components/Dialogs/ConfirmationDialog';
import AdleaksServices from '../../../Api/AdLeak/AdleaksServices';
import { ErrorToaster, SuccessToaster } from '../../../Components/Toaster/Toaster';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import { useSortable, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';

function AdBooksListing() {
  const [allAdbooks, setAllAdbooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [search, setSearch] = useState("");
  const [totalCount, setTotalCount] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [loading, setLoading] = useState(false);
  const [catLoading, setCatLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(false);
  const [isSortingEnabled, setIsSortingEnabled] = useState(false);
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const navigate = useNavigate();

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // Check if the item is dropped over another item
    if (over) {
      const activeIndex = items.findIndex(item => item._id === active.id);
      const overIndex = items.findIndex(item => item._id === over.id);

      // Only reorder if the indices are different
      if (activeIndex !== overIndex) {
        const reorderedItems = [...items];
        // Remove the active item and insert it at the new index
        const [movedItem] = reorderedItems.splice(activeIndex, 1);
        reorderedItems.splice(overIndex, 0, movedItem);
        setItems(reorderedItems);
      }
    }
  };

  const getAllAdbooks = async (catId, searchKey, page, rows) => {
    setLoading(true);
    try {
      const result = await AdBooksServices.getAllAdbooks(
        catId ? catId : null,
        searchKey ? searchKey : "",
        page ? page : 1,
        rows ? rows : 12,
        "adbook"
      );
      setAllAdbooks(result?.data?.blogs);
      setTotalCount(result?.data?.pageInfo?.totalBlogs);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  const getAllCategories = async (searchKey) => {
    setCatLoading(true);
    try {
      const result = await CategoriesServices.getAllCategories(searchKey ? searchKey : "", 1, 100, "adbook");
      const filteredCategories = result?.data?.categories?.filter((item) =>
        item.isActive == true
      );
      setCategories(filteredCategories);
    } catch (error) {
      console.log(error);
    } finally {
      setCatLoading(false);
    }
  }

  const handleLoadMore = async (event) => {
    event.preventDefault(); // Prevent page refresh
    setBtnLoading(true);
    try {
      const nextPage = page + 1;
      const result = await AdBooksServices.getAllAdbooks(
        selectedCategory,
        search,
        nextPage,
        limit,
        "adbook"
      );
      if (result?.data?.blogs?.length > 0) {
        setAllAdbooks((prevAdBooks) => [...prevAdBooks, ...result?.data?.blogs]);
      };
      setTotalCount(result?.data?.pageInfo?.totalBlogs);
      setPage(nextPage);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const result = await AdleaksServices.deleteBlog(id);
      if (result.responseCode == 200) {
        SuccessToaster(result.message)
      }
    } catch (err) {
      console.log(err);
    } finally {
      getAllAdbooks(selectedCategory, search, page, limit);
    }
  };

  const submitOrder = async () => {
    const payload = items.map((adbook, index) => ({
      _id: adbook._id,
      sortOrder: index + 1,
    }));
    const obj = {
      blogs: payload,
    }
    try {
      const result = await AdBooksServices.updateSequence(obj);
      if (result.responseCode === 200) {
        SuccessToaster("Adbooks order saved successfully!");
        setIsSortingEnabled((prev) => !prev)
      }
    } catch (error) {
      ErrorToaster("Failed to save Adbooks order.");
    } finally {
      getAllAdbooks(selectedCategory, search, 1, totalCount);
    }
  }

  const handleSortState = () => setIsSortingEnabled((prev) => !prev);

  const DraggableItem = ({ item, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({ id: item._id });

    const style = {
      transform: CSS?.Transform?.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <Grid
        item
        md={3}
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        sx={{
          minHeight: 200,
          display: 'flex',
          justifyContent: 'center',
          cursor: "grab",
        }}
      >
        <Box
          sx={{
            p: 0.5,
            borderRadius: "9px",
            boxShadow: "0px 2px 8px #d8d8d87a",
            background: "#e0e0e0",
            ":hover": {
              ".dlt-btn": {
                display: "flex"
              }
            },
            position: "relative",
          }}
        >
          <Box>
            <CardMedia
              component={"img"}
              src={baseUrl + item.image}
              sx={{ width: "100%", height: "185px", objectFit: "contain" }}
            />
          </Box>
          <Grid container alignItems={"center"} p={"0px 10px 10px 10px"}>
            <Grid item md={12}>
              <Box>
                <Typography
                  variant='body1'
                  sx={{
                    fontWeight: "bold",
                    textWrap: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {item?.title}
                </Typography>
              </Box>
            </Grid>
            <Grid item md={7.5}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <CalendarMonth sx={{ color: Colors.primary }} />
                <Typography
                  sx={{
                    color: "#ACACAC",
                  }}
                  variant="caption"
                >
                  {moment(item?.date).format("DD-MM-YYYY")}
                </Typography>
              </Box>
            </Grid>
            <Grid item md={4.5}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "5px" }}>
                <Button variant={"outlined"}>
                  Edit
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    );
  };

  useEffect(() => {
    getAllAdbooks("", "", page, limit);
    getAllCategories();
    setSelectedCategory("")
  }, []);

  useEffect(() => {
    setItems(allAdbooks);
  }, [allAdbooks]);

  return (
    <Fragment>
      <ConfirmationDialog
        open={open}
        onClose={() => setOpen(false)}
        title={"Delete Blog"}
        action={handleDelete}
        btn={"Delete"}
        secBtn={"Cancel"}
        secAction={() => setOpen(false)}
      />
      <Box
        sx={{
          borderRadius: "12px",
        }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{
            background: Colors.primaryGradient,
            p: 2,
            borderRadius: "12px",
          }}
        >
          <Grid item xs={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <IconButton onClick={() => navigate(-1)}>
                <KeyboardArrowLeft sx={{ color: Colors.white }} />
              </IconButton>
              <Typography variant="body1" fontWeight="bold" sx={{ color: Colors.white }}>
                Adbooks List
              </Typography>
            </Box>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <BreadCrumbs currentPage={["Adbooks List"]} />
          </Grid>
        </Grid>

        <Box sx={{ backgroundColor: Colors.white, mt: "20px", borderRadius: "12px" }}>
          <Grid container>

            <Grid item md={3} sx={{ backgroundColor: "#F8F8F8", borderRadius: "12px" }}>
              <Box
                sx={{
                  px: "18px",
                  py: "20px",
                  position: "sticky",
                  top: "10%"
                }}
              >
                <Typography variant="body1" sx={{ mb: "10px" }}>
                  Filter by Category
                </Typography>
                <Box sx={{ pb: "20px", my: 1.3, width: "100%" }}>
                  <SearchField searchKey={(key) => getAllCategories(key)} />
                </Box>
                <Box
                  sx={{
                    borderRadius: "13px",
                    px: "13px",
                    py: "4px",
                    mb: "15px",
                  }}
                >
                  <Box sx={{ display: "flex" }}>
                    <WorkspacesOutlined
                      sx={{ mr: "6px", fontSize: "20px", py: "2px" }}
                    />
                    <Box sx={{ py: "3px" }}>
                      <Typography variant="body2">Categories</Typography>
                    </Box>
                  </Box>
                </Box>
                {catLoading ? (
                  <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ height: "calc(100vh - 302px)", overflowY: "auto" }}>
                      <Box
                        sx={{
                          px: "14px",
                          py: "10px",
                          mb: 1,
                          cursor: "pointer",
                          backgroundColor: selectedCategory === "" ? Colors.primary : "transparent",
                          color: selectedCategory === "" ? Colors.white : Colors.black,
                          borderRadius: "13px",
                          ":hover": {
                            backgroundColor: Colors.primary,
                            color: Colors.white,
                            overflow: "hidden",
                          },
                        }}
                        onClick={() => {
                          getAllAdbooks("");
                          setSelectedCategory("");
                          // getSubCategory(item?._id)
                        }}
                      >
                        <Typography variant="body2">All</Typography>
                      </Box>
                      {categories.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            px: "14px",
                            py: "10px",
                            mb: 1,
                            cursor: "pointer",
                            backgroundColor: selectedCategory === item._id ? Colors.primary : "transparent",
                            color: selectedCategory === item._id ? Colors.white : Colors.black,
                            borderRadius: "13px",
                            ":hover": {
                              backgroundColor: Colors.primary,
                              color: Colors.white,
                              overflow: "hidden",
                            },
                          }}
                          onClick={() => {
                            getAllAdbooks(item?._id);
                            setSelectedCategory(item?._id);
                            setPage(1);
                          }}
                        >
                          <Typography variant="body2">{item.name}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item md={9} sx={{ padding: "0px" }}>
              <Box
                sx={{
                  px: "18px",
                  py: "20px",
                }}
              >
                {loading ? <CircleLoading /> : (
                  <Fragment>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ width: "25%", mx: 3 }}>
                        <SearchField searchKey={(key) => {
                          getAllAdbooks("", key, "", "")
                          setSearch(key);
                        }} />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "20px",
                        }}
                      >
                        <SecondaryButton
                          // disabled={role !== "A" ? true : false}
                          title={isSortingEnabled ? "Cancel" : "Change Order"}
                          onClick={handleSortState}
                        />
                        {isSortingEnabled && (
                          <SecondaryButton
                            // disabled={role !== "A" ? true : false}
                            title="Save Order"
                            onClick={() => submitOrder()}
                          />
                        )}
                      </Box>
                    </Box>
                    <Grid container spacing={2} sx={{ p: 2 }}>
                      {allAdbooks ? (
                        <Fragment>
                          {allAdbooks?.length > 0 ? (
                            <Fragment>
                              {isSortingEnabled ? (
                                <Grid item md={12}>
                                  <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart} collisionDetection={closestCenter}>
                                    <SortableContext items={items.map(item => item._id)} strategy={rectSortingStrategy}>
                                      <Grid container spacing={2}>
                                        {items.map((item, index) => (
                                          <DraggableItem key={item._id} item={item} index={index} />
                                        ))}
                                      </Grid>
                                    </SortableContext>
                                    <DragOverlay>
                                      {activeId ? (
                                        <Box
                                          sx={{
                                            p: 0.5,
                                            borderRadius: "9px",
                                            boxShadow: "0px 2px 8px #d8d8d87a",
                                            background: "#e0e0e0",
                                            ":hover": {
                                              ".dlt-btn": {
                                                display: "flex"
                                              }
                                            },
                                            position: "relative",
                                            cursor: "grabbing",
                                          }}
                                        >
                                          <Box>
                                            <CardMedia
                                              component={"img"}
                                              src={baseUrl + items.find(i => i._id === activeId)?.image}
                                              sx={{ width: "100%", height: "185px", objectFit: "contain" }}
                                            />
                                          </Box>
                                          <Grid container alignItems={"center"} p={"0px 10px 10px 10px"}>
                                            <Grid item md={12}>
                                              <Box>
                                                <Typography
                                                  variant='body1'
                                                  sx={{
                                                    fontWeight: "bold",
                                                    textWrap: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis"
                                                  }}
                                                >
                                                  {items.find(i => i._id === activeId)?.title}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                            <Grid item md={7.5}>
                                              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                <CalendarMonth sx={{ color: Colors.primary }} />
                                                <Typography
                                                  sx={{
                                                    color: "#ACACAC",
                                                  }}
                                                  variant="caption"
                                                >
                                                  {moment(items.find(i => i._id === activeId)?.date).format("DD-MM-YYYY")}
                                                </Typography>
                                              </Box>
                                            </Grid>
                                            <Grid item md={4.5}>
                                              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "5px" }}>
                                                <Button
                                                  variant={"outlined"}
                                                  onClick={() => navigate(`/adbook-edit/${items.find(i => i._id === activeId)?._id}`, { state: items.find(i => i._id === activeId) })}
                                                >
                                                  Edit
                                                </Button>
                                              </Box>
                                            </Grid>
                                          </Grid>
                                        </Box>
                                      ) : null}
                                    </DragOverlay>
                                  </DndContext>
                                </Grid>
                              ) : (
                                <Fragment>
                                  {allAdbooks.map((item, index) => (
                                    <Grid key={index} item md={3}>
                                      <Box
                                        sx={{
                                          p: 0.5,
                                          borderRadius: "9px",
                                          boxShadow: "0px 2px 8px #d8d8d87a",
                                          background: "#fff",
                                          ":hover": {
                                            ".dlt-btn": {
                                              display: "flex"
                                            }
                                          },
                                          position: "relative",
                                        }}
                                      >
                                        <Box>
                                          <CardMedia
                                            component={"img"}
                                            src={baseUrl + item.image}
                                            sx={{ width: "100%", height: "185px", objectFit: "contain" }}
                                          />
                                        </Box>
                                        <Grid container alignItems={"center"} p={"0px 10px 10px 10px"}>
                                          <Grid item md={12}>
                                            <Box>
                                              <Typography
                                                variant='body1'
                                                sx={{
                                                  fontWeight: "bold",
                                                  textWrap: "nowrap",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis"
                                                }}
                                              >
                                                {item?.title}
                                              </Typography>
                                            </Box>
                                          </Grid>
                                          <Grid item md={7.5}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                              <CalendarMonth sx={{ color: Colors.primary }} />
                                              <Typography
                                                sx={{
                                                  color: "#ACACAC",
                                                }}
                                                variant="caption"
                                              >
                                                {moment(item?.date).format("DD-MM-YYYY")}
                                              </Typography>
                                            </Box>
                                          </Grid>
                                          <Grid item md={4.5}>
                                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "5px" }}>
                                              <Button
                                                variant={"outlined"}
                                                onClick={() => navigate(`/adbook-edit/${item?._id}`, { state: item })}
                                              >
                                                Edit
                                              </Button>
                                            </Box>
                                          </Grid>
                                        </Grid>
                                        <IconButton
                                          onClick={() => {
                                            setOpen(true);
                                            setId(item?._id);
                                          }}
                                          className='dlt-btn'
                                          sx={{
                                            background: Colors.primary,
                                            ":hover": {
                                              background: Colors.primary,
                                            },
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            display: "none",
                                            height: "40px",
                                            width: "40px"
                                          }}
                                        >
                                          <Delete sx={{ color: Colors.white }} />
                                        </IconButton>
                                      </Box>
                                    </Grid>
                                  ))}
                                </Fragment>
                              )}
                            </Fragment>
                          ) : (
                            <Box
                              sx={{
                                width: 1,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <Typography variant="h6">No data found</Typography>
                            </Box>
                          )}
                        </Fragment>
                      ) : (
                        <Box sx={{ width: 1, textAlign: "center" }}>
                          <CircleLoading />
                        </Box>
                      )}
                    </Grid>
                    <Box
                      sx={{
                        display: "flex", justifyContent: "center"
                      }}
                    >
                      {totalCount !== allAdbooks?.length && (
                        <PrimaryButton
                          title={"Load More"}
                          loading={btnLoading}
                          onClick={(e) => handleLoadMore(e)}
                        />
                      )}
                    </Box>
                  </Fragment>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Fragment >
  )
}

export default AdBooksListing